import { action } from 'typesafe-actions';
import {
    ResponseModalObject,
    DynamicObject,
} from '../../utils/commonInterfaces';
import { CommonFiltersActionTypes, PageView } from './types';

/**
 * Action called for triggering the saga for saving the Applied filters view.
 * @param payload
 * @param callback - function called after saving - useful for refetching the company details
 * and showing alert modals
 */
export const saveAppliedFiltersViewAction = (
    payload: any,
    callback?: (response: ResponseModalObject) => void
) =>
    action(CommonFiltersActionTypes.SAVE_APPLIED_FILTERS_VIEW, {
        payload,
        callback,
    });

/**
 * Action called for triggering the saga for saving the Applied filters view.
 * @param payload
 * @param callback - function called after saving - useful for refetching the company details
 * and showing alert modals
 */
export const deleteAppliedFiltersViewAction = (
    payload: { Id: string; PageName: string | undefined },
    callback?: (response: ResponseModalObject) => void
) =>
    action(CommonFiltersActionTypes.DELETE_APPLIED_FILTERS_VIEW, {
        payload,
        callback,
    });

/**
 * Action for fetching the Page Views (table filters) per page
 * @param pageName - page on where to get the Page Views from
 * @param isDashboard - determines if the result is for dashboard / view
 * @param notSameCompany - determines if the fetch is due to switching companies
 * @param callback - callback function that handles the returned pageview list
 */
export const fetchPageAppliedFiltersViewListRequestAction = (
    pageName: string,
    isDashboard: boolean,
    notSameCompany: string | boolean,
    callback?: (data: PageView[]) => void
) =>
    action(
        CommonFiltersActionTypes.FETCH_PAGE_APPLIED_FILTERS_VIEW_LIST_REQUEST,
        {
            pageName,
            isDashboard,
            notSameCompany,
            callback,
        }
    );

/**
 * Function that updates the refetch page view indicator.
 * @param willRefetch - boolean indicator to refetch or not
 * @param defaultPageViewValue - optional value, the latest added filter
 */
export const updateRefetchPageViewsAction = (
    willRefetch: boolean,
    defaultPageViewValue?: string
) =>
    action(CommonFiltersActionTypes.UPDATE_REFETCH_PAGE_VIEWS, {
        willRefetch,
        defaultPageViewValue,
    });

export const updatePageViewsSelectedAction = (
    pageName: string,
    pageView: any
) =>
    action(CommonFiltersActionTypes.UPDATE_PAGE_VIEWS_SELECTED, {
        pageName,
        pageView,
    });

/**
 * Function that triggers the extraction process for downloading to excel.
 * @param query - query to use for handling the extraction process (may be changed to string)
 * @param callback - callback function called after a success/error
 */
export const downloadToExcelAction = (
    payload: DynamicObject,
    callback?: (response: ResponseModalObject) => void
) =>
    action(CommonFiltersActionTypes.DOWNLOAD_TO_EXCEL, {
        payload,
        callback,
    });

/**
 * Function that gets the presigned URL for the excel report file stored in S3.
 * @param payload - body payload for the request
 * @param callback - callback function called after a success/error
 */
export const getPresignedUrlDownloadReportReadyAction = (
    payload: DynamicObject,
    callback?: (response: DynamicObject) => void,
    refetchCount: number = 1
) =>
    action(CommonFiltersActionTypes.GET_PRESIGNED_URL_FOR_REPORT_READY, {
        payload,
        callback,
        refetchCount
    });

/**
 * Function that gets the custom fields filter list of a specific page.
 * @param customFieldType - type of custom fields filter list to get
 * @param callback - callback function called after a success/error
 */
export const getCustomFieldsFilterListByTypes = (
    customFieldTypes: string[],
    callback?: (response: DynamicObject) => void
) =>
    action(CommonFiltersActionTypes.GET_CUSTOM_FIELDS_FILTER_LIST, {
        customFieldTypes,
        callback,
    });

/**
 * Action called for triggering the saga for getting the presigned url for uploading the attachment.
 * @param payload
 * @param callback - function called after saving
 */
export const getAttachmentPresignedUrlAction = (
    payload: DynamicObject,
    callback?: (response: ResponseModalObject) => void
) =>
    action(CommonFiltersActionTypes.GET_ATTACHMENT_PRESIGNED_URL, {
        payload,
        callback,
    });

/**
 * Action called for triggering the saga for deleting the attachment.
 * @param payload
 * @param callback - function called after saving
 */
export const deleteAttachmentAction = (
    payload: DynamicObject,
    callback?: (response: ResponseModalObject) => void
) =>
    action(CommonFiltersActionTypes.DELETE_ATTACHMENT, {
        payload,
        callback,
    });

/**
 * Function that updates the refetch page view indicator.
 * @param isEditing - boolean indicator if attachment is editing or not
 */
export const updateAttachmentEditingAction = (isEditing: boolean) =>
    action(CommonFiltersActionTypes.UPDATE_ATTACHMENT_EDITING, isEditing);

/**
 * Function that updates the locale redux state.
 * @param locale
 */
export const setLocaleAction = (locale: string) =>
    action(CommonFiltersActionTypes.SET_LOCALE, locale);

/**
 * Function for calling the config file for the demo options.
 */
export const getDemoOptionsConfigAction = () =>
    action(CommonFiltersActionTypes.GET_DEMO_OPTIONS_CONFIG);

export const updateDemoOptionsConfigAction = (config: DynamicObject[]) =>
    action(CommonFiltersActionTypes.UPDATE_DEMO_OPTIONS_CONFIG, config);

export const loadLuckysheetAction = () => action(CommonFiltersActionTypes.LOAD_LUCKYSHEET);
